import {
    type FC,
    type ReactNode,
    useState,
    useCallback,
    cloneElement,
    isValidElement,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';
import { triggerConstants } from 'dibs-tracking';
import { SellerBrandingTrackingText } from 'dibs-buyer-layout/exports/SellerBrandingTrackingText.tsx';
import {
    trackSellerBrandingSiderailClick,
    type SellerBrandingTrackingTrigger,
} from 'dibs-buyer-layout/exports/sellerBrandingTracking.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpSharedSellerDetailsModalLazy } from '../PdpSharedSellerDetailsModal/PdpSharedSellerDetailsModalLazy.tsx';

import { type PdpSharedSellerBrandingInfo_seller$key } from './__generated__/PdpSharedSellerBrandingInfo_seller.graphql.ts';

import styles from './PdpSharedSellerBrandingInfo.module.css';

const sellerFragment = graphql`
    fragment PdpSharedSellerBrandingInfo_seller on Seller {
        serviceId
        liveStorefrontProfile {
            photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
        }
        sellerProfile {
            company
        }
    }
`;

type Props = {
    seller: PdpSharedSellerBrandingInfo_seller$key | null | undefined;
    showSellerBranding?: boolean;
    linkSellerName?: boolean;
    stackedLayout?: boolean;
    wrapperClassName?: string;
    imageClassName?: string;
    titleClassName?: string;
    noImageTitleClassName?: string;
    onNavigateToSellerSection?: () => void;
    trackingTrigger?: SellerBrandingTrackingTrigger;
    children?: ReactNode;
};

export const PdpSharedSellerBrandingInfo: FC<Props> = ({
    seller: sellerRef,
    showSellerBranding,
    linkSellerName = false,
    stackedLayout = false,
    wrapperClassName,
    imageClassName,
    titleClassName,
    noImageTitleClassName,
    onNavigateToSellerSection,
    trackingTrigger,
    children,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const { isMobile } = useServerVarsContext();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = useCallback(() => setIsModalOpen(true), []);
    const closeModal = useCallback(() => setIsModalOpen(false), []);

    const handleSellerNameClick = useCallback(() => {
        if (trackingTrigger === triggerConstants.TRIGGER_SIDERAIL) {
            trackSellerBrandingSiderailClick();
        }

        if (isMobile && !onNavigateToSellerSection) {
            openModal();
            return;
        }

        onNavigateToSellerSection?.();
    }, [isMobile, openModal, onNavigateToSellerSection, trackingTrigger]);

    if (!showSellerBranding) {
        return children;
    }

    const { liveStorefrontProfile, sellerProfile, serviceId } = seller || {};
    const photo = liveStorefrontProfile?.photos?.[0];

    const sellerImagePath = photo?.status === 'ACTIVE' ? photo.path : null;
    const showSellerImage = sellerImagePath && (!linkSellerName || stackedLayout);
    const isInlineLinkedLayout = linkSellerName && !stackedLayout;
    const company = sellerProfile?.company;

    let companyName: ReactNode = null;
    if (company) {
        const companyNameContent = (
            <SellerBrandingTrackingText trackingTrigger={trackingTrigger}>
                {company}
            </SellerBrandingTrackingText>
        );

        companyName = linkSellerName ? (
            <Link
                className={dibsCss.sassyFontBodySmallHeavy}
                onClick={handleSellerNameClick}
                underline="none"
                dataTn="pdp-sidebar-seller-name-link"
            >
                {companyNameContent}
            </Link>
        ) : (
            companyNameContent
        );
    }

    const companyNameElement = companyName && (
        <div
            className={classnames(
                titleClassName ?? dibsCss.sassyFontBodySmallHeavy,
                {
                    [dibsCss.mbXxsmall]: !linkSellerName || stackedLayout,
                },
                !showSellerImage && noImageTitleClassName
            )}
        >
            {companyName}
        </div>
    );

    const reviewsContent =
        isInlineLinkedLayout && isValidElement<{ sellerName?: ReactNode }>(children)
            ? cloneElement(children, { sellerName: companyNameElement })
            : children;

    return (
        <>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.itemsStart,
                    dibsCss.gapXsmall,
                    wrapperClassName
                )}
            >
                {showSellerImage && (
                    <img
                        alt={company || ''}
                        src={getSrcsetUrl(sellerImagePath, 150)}
                        className={classnames(dibsCss.roundedFull, imageClassName, {
                            [styles.sellerImage]: !imageClassName,
                        })}
                    />
                )}
                <div
                    className={classnames(dibsCss.flex, dibsCss.flex1, {
                        [dibsCss.flexCol]: !linkSellerName || stackedLayout,
                        [dibsCss.itemsCenter]: isInlineLinkedLayout,
                        [dibsCss.flexWrap]: isInlineLinkedLayout,
                        [dibsCss.gapXsmall]: isInlineLinkedLayout,
                    })}
                >
                    {(!linkSellerName || stackedLayout) && companyNameElement}
                    {reviewsContent}
                </div>
            </div>
            {linkSellerName && serviceId && (
                <PdpSharedSellerDetailsModalLazy
                    sellerId={serviceId}
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    showSellerBranding
                />
            )}
        </>
    );
};
